import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LANGS } from "@/i18n";

const SITE = "Doctors Vietnam";
// Permanent production canonical base. Preview/staging never becomes canonical.
const BASE = "https://doctorsvietnam.com";
const PRODUCTION_HOST = "doctorsvietnam.com";

// Detect preview/staging vs production. Only the real production host is indexable.
const isProduction = () =>
  typeof window !== "undefined" &&
  (window.location.hostname === PRODUCTION_HOST ||
    window.location.hostname === `www.${PRODUCTION_HOST}`);

function setMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(items) {
  document.head.querySelectorAll('script[data-seo-jsonld="true"]').forEach((el) => el.remove());
  (items || []).forEach((obj) => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-seo-jsonld", "true");
    el.text = JSON.stringify(obj);
    document.head.appendChild(el);
  });
}

function setAlternates(path) {
  document.head.querySelectorAll('link[data-seo-alt="true"]').forEach((el) => el.remove());
  const add = (hreflang, href) => {
    const el = document.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("hreflang", hreflang);
    el.setAttribute("href", href);
    el.setAttribute("data-seo-alt", "true");
    document.head.appendChild(el);
  };
  LANGS.forEach((l) => add(l, `${BASE}/${l}${path}`));
  add("x-default", `${BASE}/en${path}`);
}

/**
 * Lightweight per-page SEO: title, meta description, canonical (always doctorsvietnam.com),
 * hreflang alternates (+ x-default), Open Graph, JSON-LD.
 * Pass `exact` to use `title` verbatim. `path` is the un-prefixed path (e.g. "/contact").
 *
 * On preview/staging hosts the `robots` meta is forced to `noindex,nofollow` so the
 * preview never competes with DoctorsVietnam.com in search results.
 */
export const Seo = ({ title, description, path = "", exact = false, jsonLd = [], ogTitle, ogDescription }) => {
  const { i18n } = useTranslation();
  const lang = LANGS.includes(i18n.language) ? i18n.language : "en";
  const normPath = path === "/" ? "" : path;
  const fullTitle = exact && title
    ? title
    : (title ? `${title} | ${SITE}` : `${SITE} — Independent Healthcare Coordination in Vietnam`);
  const url = `${BASE}/${lang}${normPath}`;
  const jsonLdKey = JSON.stringify(jsonLd);
  useEffect(() => {
    document.title = fullTitle;
    setMeta("name", "description", description);
    setMeta("name", "robots", isProduction() ? "index, follow" : "noindex, nofollow");
    setMeta("property", "og:title", ogTitle || fullTitle);
    setMeta("property", "og:description", ogDescription || description);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:url", url);
    setMeta("property", "og:site_name", SITE);
    setMeta("property", "og:locale", lang);
    setMeta("name", "twitter:card", "summary_large_image");
    setCanonical(url);
    setAlternates(normPath);
    setJsonLd(jsonLd);
  }, [fullTitle, description, url, jsonLdKey, lang, ogTitle, ogDescription]);
  return null;
};

export default Seo;
